import React from 'react';

/**
 * Gradient Icon component for displaying icons with gradient backgrounds
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.icon - The icon to display
 * @param {string} [props.variant='primary'] - Color variant (primary, blue, purple, green, red, orange)
 * @param {string} [props.size='md'] - Size of the icon container (sm, md, lg)
 * @param {string} [props.shape='square'] - Shape of the container (square, circle)
 * @param {string} [props.className] - Additional CSS classes
 */
const GradientIcon = ({
  icon,
  variant = 'primary',
  size = 'md',
  shape = 'square',
  className = '',
  ...rest
}) => {
  // Base classes
  const baseClasses = 'flex items-center justify-center shadow-sm transition-all duration-200';

  // Color variants
  const variantClasses = {
    primary: 'bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700',
    blue: 'bg-gradient-to-r from-blue-500 to-cyan-600 dark:from-blue-600 dark:to-cyan-700',
    purple: 'bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700',
    green: 'bg-gradient-to-r from-emerald-600 to-green-700 dark:from-emerald-700 dark:to-green-800',
    red: 'bg-gradient-to-r from-rose-600 to-red-600 dark:from-rose-700 dark:to-red-700',
    orange: 'bg-gradient-to-r from-orange-500 to-amber-600 dark:from-orange-600 dark:to-amber-700'
  };

  // Size variants
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  // Icon size based on container size
  const iconSizeMap = {
    sm: 16,
    md: 22,
    lg: 28
  };

  // Shape variants
  const shapeClasses = {
    square: 'rounded-lg',
    circle: 'rounded-full'
  };

  // Get the variant class
  const colorClass = variantClasses[variant] || variantClasses.primary;
  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const shapeClass = shapeClasses[shape] || shapeClasses.square;
  const iconSize = iconSizeMap[size] || iconSizeMap.md;

  // Clone the icon element with the right size
  const iconElement = React.isValidElement(icon)
    ? React.cloneElement(icon, { 
        size: iconSize,
        className: `text-white ${icon.props.className || ''}` 
      })
    : null;

  return (
    <div className={`${baseClasses} ${colorClass} ${sizeClass} ${shapeClass} ${className}`} {...rest}>
      {iconElement}
    </div>
  );
};

export default GradientIcon; 